import { useAppTheme } from "@hooks/useAppTheme";
import Box from "@memorang/ui/components/Box";
import { useEffect, useRef } from "react";
import { ScrollView, View } from "react-native";
import { Card, Text } from "react-native-paper";
import { UI_TEXT } from "../constants";
import { MENU_OPTIONS } from "../constants/calculations";
import type { CalculationMode, MathOperation } from "../types";

interface DisplayProps {
	display: string;
	mode: CalculationMode;
	selectedIndex: number;
	input: string;
	result: string;
	previousValue: string;
	operation: MathOperation;
	memory: number;
	isTimerRunning: boolean;
	timerSeconds: number;
}

const Display = ({
	display,
	mode,
	selectedIndex,
	input,
	result,
	previousValue,
	operation,
	memory,
	isTimerRunning,
	timerSeconds,
}: DisplayProps) => {
	const scrollViewRef = useRef<ScrollView>(null);
	const currentMenuItems = MENU_OPTIONS[mode];

	useEffect(() => {
		if (scrollViewRef.current && selectedIndex >= 0) {
			const itemHeight = 28;
			const containerHeight = 90;
			const itemY = selectedIndex * itemHeight;
			const scrollY = Math.max(0, itemY - containerHeight / 2 + itemHeight / 2);

			scrollViewRef.current.scrollTo({
				y: scrollY,
				animated: true,
			});
		}
	}, [selectedIndex]);

	const theme = useAppTheme();

	return (
		<Card
			style={{
				backgroundColor: theme.palette.calculator.displayBackground,
				minHeight: 300,
				flex: 1,
			}}
		>
			<Card.Content>
				<View
					style={{
						flexDirection: "row",
						justifyContent: "space-between",
					}}
				>
					<Text variant="labelMedium">{UI_TEXT.E6B_LABEL}</Text>
					<Text variant="labelMedium">
						{isTimerRunning
							? `${Math.floor(timerSeconds / 60)}:${(timerSeconds % 60).toString().padStart(2, "0")}`
							: UI_TEXT.DEFAULT_TIME}
					</Text>
					<Text variant="labelMedium">{UI_TEXT.BATTERY_LABEL}</Text>
				</View>

				<Text variant="titleMedium" style={{ textAlign: "center" }}>
					{display}
				</Text>

				<ScrollView
					ref={scrollViewRef}
					style={{
						maxHeight: 180,
						paddingTop: 8,
					}}
					showsVerticalScrollIndicator={false}
				>
					<Box gap={4}>
						{currentMenuItems && currentMenuItems.length > 0 ? (
							currentMenuItems.map((item, index) => (
								<Box
									key={item.type}
									style={{
										flex: 1,
										minHeight: 32,
										justifyContent: "center",
										backgroundColor:
											index === selectedIndex ? theme.colors.white : undefined,
										paddingHorizontal: 8,
										borderRadius: 4,
									}}
								>
									<Text
										key={item.type}
										style={{
											fontSize: 14,
											fontWeight: index === selectedIndex ? "bold" : "normal",
											color:
												index === selectedIndex
													? theme.colors.black
													: theme.colors.white,
										}}
									>
										{item.label}
									</Text>
								</Box>
							))
						) : (
							<Text variant="labelSmall">{UI_TEXT.NO_MENU_ITEMS}</Text>
						)}
					</Box>
				</ScrollView>

				<Box style={{ minHeight: 40, paddingTop: 8 }}>
					{input && !result && (
						<Text
							variant="labelSmall"
							style={{ color: theme.palette.calculator.inputTextColor }}
						>
							{UI_TEXT.INPUT_LABEL}
							{input}
						</Text>
					)}

					{operation && previousValue && (
						<Text
							variant="labelSmall"
							style={{ color: theme.palette.calculator.operationTextColor }}
						>
							{previousValue} {operation}
						</Text>
					)}

					{result && (
						<Text
							variant="labelSmall"
							style={{ color: theme.palette.calculator.resultTextColor }}
						>
							{result}
						</Text>
					)}

					{memory !== 0 && (
						<Text
							variant="labelSmall"
							style={{ color: theme.palette.calculator.memoryTextColor }}
						>
							{UI_TEXT.MEMORY_LABEL}
							{memory.toFixed(2)}
						</Text>
					)}
				</Box>
			</Card.Content>
		</Card>
	);
};

export default Display;
